import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, PictureOutlined, CheckCircleOutlined, CloseCircleOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import * as galleryApi from '../../api/galleryApi';
import GalleryForm from '../../components/admin/gallery/GalleryForm';
import GalleryTable from '../../components/admin/gallery/GalleryTable';
import Modal from '../../components/common/Modal';

const { Option } = Select;

const GalleryManagement = () => {
  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch data
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      setLoading(true);
      const response = await galleryApi.getGalleryImages();
      
      console.log('Gallery API response:', response);
      
      // The API returns { images, totalPages, currentPage }
      if (response.images && Array.isArray(response.images)) {
        setGalleryImages(response.images);
      } else {
        // Fallback to empty array if no valid data format is found
        setGalleryImages([]);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      toast.error('Failed to fetch gallery images');
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Gallery handlers
  const handleCreate = () => {
    setEditingImage(null);
    setShowForm(true);
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery image?')) return;

    try {
      await galleryApi.deleteGalleryImage(id);
      toast.success('Gallery image deleted successfully');
      fetchGalleryImages();
    } catch (error) {
      console.error('Error deleting gallery image:', error);
      toast.error('Failed to delete gallery image');
    }
  };

  const handleToggle = async (id) => {
    try {
      await galleryApi.toggleGalleryImageStatus(id);
      toast.success('Gallery image status updated');
      fetchGalleryImages();
    } catch (error) {
      console.error('Error toggling gallery image status:', error);
      toast.error('Failed to update gallery image status');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingImage(null);
    fetchGalleryImages();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingImage(null);
  };

  // Filter gallery images
  const filteredGalleryImages = Array.isArray(galleryImages)
    ? galleryImages.filter(image => {
        const matchesSearch = image.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             image.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = statusFilter === 'all' || 
                             (statusFilter === 'active' && image.isActive) ||
                             (statusFilter === 'inactive' && !image.isActive);
        return matchesSearch && matchesFilter;
      })
    : [];

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              {/* <h1 className="text-2xl font-bold text-gray-800">Gallery Management</h1>
              <p className="text-gray-600">Manage photos and videos for your gallery</p> */}
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                backgroundColor: '#470172',
                borderColor: '#470172',
                height: 'auto',
                padding: '10px 20px'
              }}
            >
              Add Media
            </Button>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                size="large"
                placeholder="Search by title or description..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#470172'
                }}
              />
            </div>
            <div>
              <Select
                size="large"
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
                style={{
                  width: '100%',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#470172'
                }}
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Total Media</span>}
                value={Array.isArray(galleryImages) ? galleryImages.length : 0}
                prefix={<PictureOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Active</span>}
                value={Array.isArray(galleryImages) ? galleryImages.filter(i => i.isActive).length : 0}
                prefix={<CheckCircleOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Inactive</span>}
                value={Array.isArray(galleryImages) ? galleryImages.filter(i => !i.isActive).length : 0}
                prefix={<CloseCircleOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Videos</span>}
                value={Array.isArray(galleryImages) ? galleryImages.filter(i => i.mediaType === 'video').length : 0}
                prefix={<VideoCameraOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
          </div>

          {/* Gallery Table */}
          <Card
            title={
              <div>
                <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                  List of all gallery media
                </p>
              </div>
            }
            extra={
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={fetchGalleryImages}
                disabled={loading}
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#470172',
                  borderColor: '#470172'
                }}
              >
                Refresh
              </Button>
            }
          >
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
              </div>
            ) : filteredGalleryImages.length > 0 ? (
              <GalleryTable
                galleryImages={filteredGalleryImages}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggle}
              />
            ) : (
              <Empty
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                    {searchTerm || statusFilter !== 'all' ? 'No gallery media found' : 'Get started by adding your first photo or video.'}
                  </span>
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  size="large"
                  style={{
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    backgroundColor: '#470172',
                    borderColor: '#470172',
                    height: 'auto',
                    padding: '10px 20px'
                  }}
                >
                  Add Media
                </Button>
              </Empty>
            )}
          </Card>
        </div>
      </div>

      {showForm && (
        <Modal 
          isOpen={showForm} 
          onClose={handleFormCancel} 
          title={editingImage ? 'Edit Gallery Item' : 'Add New Media'}
          width="90%"
          style={{ top: 20 }}
          bodyStyle={{ height: 'calc(90vh - 100px)', overflow: 'auto' }}
        >
          <GalleryForm
            galleryImage={editingImage}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </Modal>
      )}
    </>
  );
};

export default GalleryManagement;
